/**
 * Geometry for the "still being worked on" halftone on a draft medication card.
 *
 * The visual is a grid of dots that grows and darkens from left to right across
 * the card header. It used to be drawn as one `<Circle>` per dot, which is one
 * NATIVE VIEW per dot — 300–600 per card, several thousand when a photo run
 * streams fifteen cards onto the hub in a second. Mounting them stalled the UI
 * thread, and LogRocket's session capture then rasterised every one of them (each
 * with its own opacity, so each its own CoreGraphics transparency layer) on every
 * snapshot for as long as the run lasted. That was TASKHEALTH-MOBILE-3Y / -44:
 * 180 "App Hanging" events from 18 nurses in the first two weeks of September
 * 2026, every one on this screen with photos being read.
 *
 * The same picture is now ONE path — every dot a circle sub-path, the radius
 * ramp in the geometry — filled with a horizontal gradient that carries the
 * opacity ramp. One native node instead of hundreds, and one transparency layer
 * for LogRocket instead of hundreds.
 *
 * Pure so it can be unit-tested; the component just feeds it a size.
 */

/** Grid pitch in points. */
export const HALFTONE_STEP = 7;
/** Dots start a third of the way across so the medication name stays on clean ground. */
export const HALFTONE_RAMP_START = 0.32;
/** Opacity of the faintest (leftmost) dots. Matches the old per-circle minimum. */
export const HALFTONE_MIN_OPACITY = 0.14;
/** Opacity of the darkest (rightmost) dots. Matches the old per-circle maximum (0.14 + 0.5). */
export const HALFTONE_MAX_OPACITY = 0.64;

const MIN_RADIUS = 0.7;
const RADIUS_GROWTH = 1.15;

function fmt(n: number): string {
    // Two decimals is well under a device pixel and keeps a 600-dot path around 30 KB.
    return String(Math.round(n * 100) / 100);
}

export interface HalftonePath {
    /** SVG path data: one closed circle sub-path per dot. Empty when nothing fits. */
    d: string;
    /** Number of dots in the path — what the old renderer would have mounted as views. */
    dotCount: number;
}

/**
 * Build the dot grid for a header of `width` × `height` points.
 *
 * Column x is included only once its ramp is positive; the ramp runs linearly
 * from HALFTONE_RAMP_START × width to the right edge. Alternate columns are
 * shifted down half a pitch so the grid doesn't read as a screen door.
 */
export function halftoneDotsPath(
    width: number,
    height: number,
    step = HALFTONE_STEP
): HalftonePath {
    if (!(width > 0) || !(height > 0) || !(step > 0)) {
        return { d: "", dotCount: 0 };
    }
    const parts: string[] = [];
    for (let x = step / 2; x < width; x += step) {
        const ramp = Math.max(0, (x / width - HALFTONE_RAMP_START) / (1 - HALFTONE_RAMP_START));
        if (ramp <= 0) {
            continue;
        }
        const r = MIN_RADIUS + ramp * RADIUS_GROWTH;
        const columnShift = Math.round(x / step) % 2 === 0 ? 0 : step / 2;
        for (let y = step / 2; y < height; y += step) {
            const cy = y + columnShift;
            if (cy > height) {
                continue;
            }
            // Circle as two half-turn arcs, starting at the leftmost point.
            parts.push(
                `M${fmt(x - r)},${fmt(cy)}a${fmt(r)},${fmt(r)} 0 1,0 ${fmt(2 * r)},0a${fmt(r)},${fmt(r)} 0 1,0 ${fmt(-2 * r)},0Z`
            );
        }
    }
    return { d: parts.join(""), dotCount: parts.length };
}
